import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  completeProject,
  createProject,
  CreateProjectPayload,
  getActiveProjects,
  getProject,
  getProjects,
  reopenProject,
} from "@/api/projects";
import { Did } from "@/types";

/**
 * Hook to fetch all projects
 */
export const useProjects = () => {
  return useQuery({
    queryKey: ["projects"],
    queryFn: getProjects,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch active projects only
 */
export const useActiveProjects = () => {
  return useQuery({
    queryKey: ["projects", "active"],
    queryFn: getActiveProjects,
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch a single project with details
 */
export const useProject = (projectId: Did | undefined) => {
  return useQuery({
    queryKey: ["projects", projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

const invalidateProjects = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ["projects"] });

export const useCreateProjectMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProjectPayload) => createProject(payload),
    onSuccess: () => invalidateProjects(qc),
  });
};

export const useCompleteProjectMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Did) => completeProject(id),
    onSuccess: () => invalidateProjects(qc),
  });
};

export const useReopenProjectMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Did) => reopenProject(id),
    onSuccess: () => invalidateProjects(qc),
  });
};
